import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

// Avoids TS2589 from api.d.ts deep type chain
export type ItemPriceInput =
  | number
  | { default: number; locations?: Record<string, number> };

export interface ItemMutationData {
  price: ItemPriceInput;
  imageUrl?: string;
  taxCategoryId?: Id<"taxCategories">;
  salesCategoryId?: Id<"salesCategories">;
  categories: Id<"categories">[];
  name: string;
  shortName: string;
  description: string;
  isOutOfStock: boolean;
  modifierGroups: Id<"modifierGroups">[];
  useCategoryModifiers: boolean;
  availableOnline?: boolean;
  onlinePrice?: number;
}

export const itemsUpdateRef = api.items.update;

export const itemsCloneAndUpdateRef = api.items.cloneAndUpdate;

/**
 * Shape of itemData from api.items.getOne -- manually typed to bypass TS2589
 * from FunctionReturnType deep inference.
 */
export interface ItemDataForForm {
  id: Id<"items">;
  name: string;
  shortName: string;
  imageUrl?: string;
  description: string;
  price: number | Record<string, number>;
  isOutOfStock: boolean;
  availableOnline?: boolean;
  onlinePrice?: number;
  useCategoryModifiers: boolean;
  salesCategoryId?: Id<"salesCategories">;
  taxCategoryId?: Id<"taxCategories">;
  categoryRelations: Array<{
    category: {
      _id: string;
      name: string;
      itemCount: string | number;
      menuRelations: Array<{
        menu: { _id: string; name: string };
      }>;
      [key: string]: unknown;
    };
  }>;
  modifierGroupRelations: Array<{
    modifierGroupId: Id<"modifierGroups">;
  }>;
  [key: string]: unknown;
}

// -- Prop types manually typed to bypass TS2589 from FunctionReturnType --

export interface CategoryForMenu {
  id: Id<"menuCategories">;
  categoryId: Id<"categories">;
  name: string;
  itemCount: number | null;
  menuRelations: {
    menu: { id: string; _id: string; name: string; [key: string]: unknown };
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export interface TaxCategoryForForm {
  _id: Id<"taxCategories">;
  name: string;
  value: number;
  [key: string]: unknown;
}

export interface ModifierForForm {
  name: string;
  additionalCost?: number | string;
}

export interface SubModifierGroupForForm {
  id: string;
  name: string;
  modifiers: ModifierForForm[];
}

export interface ModifierGroupForForm {
  _id: Id<"modifierGroups">;
  name: string;
  modifiers: ModifierForForm[];
  subModifierGroups: SubModifierGroupForForm[];
}

export interface LocationForForm {
  id: Id<"locations">;
  name: string;
  logoUrl?: string;
  [key: string]: unknown;
}

export interface ItemFormData {
  name: string;
  shortName: string;
  imageUrl: string | null;
  description: string;
  price: string | Record<string, string>;
  isOutOfStock: boolean;
  availableOnline: boolean;
  onlinePrice: string;
  categories: Id<"categories">[];
  modifierGroups: Id<"modifierGroups">[];
  useCategoryModifiers: boolean;
  devicesIds: string[];
  taxCategoryId: string | null;
  salesCategoryId: Id<"salesCategories"> | null;
}

export type Price = string | Record<string, string>;
